<script>
import ClientAiSideBar from "./components/ClientAiSideBar.vue";
import SpeechToText from "@/pages/admin/ai_assistant/components/SpeechToText.vue";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";
import Button from "@/shared/ui/button/Button.vue";
import { sendChat } from "@/shared/api/apiClientAi";
import { getTemplateItem } from "@/entities/template";
import { CATEGORY_CODES } from "@/shared/lib/constants";
import { Loading } from "@element-plus/icons-vue";
import { safeMarkdown, safeHtml } from "@/shared/lib/markdown/safeMarkdown";
import moment from "moment";

export default {
  name: "ClientAiChat",
  components: { ClientAiSideBar, SpeechToText, SvgIcon, Button, Loading },
  data() {
    return {
      loading: false,
      sessions: [],
      currentSession: null,
      abortController: null,
      message: "",
      inputMode: "text",
      sidebarCollapsed: false,
      isAtBottom: true,
    };
  },
  created() {
    const stored = localStorage.getItem("client_ai_chat_sessions");
    let parsedSessions = [];
    if (stored) {
      try {
        parsedSessions = JSON.parse(stored);
      } catch (e) {
        console.error(e);
      }
    }
    const oneWeekAgo = moment().subtract(7, "days").valueOf();
    this.sessions = parsedSessions.filter((s) => s.createdAt >= oneWeekAgo);
    this.createNewSession();
  },
  mounted() {
    this.$refs.textarea?.focus();
    this.attachScrollListener();
  },
  beforeUnmount() {
    this.detachScrollListener();
    if (this._scrollRAF) cancelAnimationFrame(this._scrollRAF);
    this._scrollCancel?.();
  },
  watch: {
    sessions: {
      deep: true,
      handler(newVal) {
        const withMessages = newVal.filter((s) => s.messages?.length > 0);
        localStorage.setItem(
          "client_ai_chat_sessions",
          JSON.stringify(withMessages)
        );
      },
    },
    message() {
      this.$nextTick(() => this.adjustTextareaHeight());
    },
    history: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this._onScroll?.();
        });
      },
    },
  },
  computed: {
    history() {
      return this.currentSession ? this.currentSession.messages : [];
    },
  },
  methods: {
    renderMessage(msg) {
      if (!msg) return "";
      // safeMarkdown уже санитизирует. Постпроцессинг с подменой <strong> на
      // блок-цитату делается на уже-чистом HTML; повторный safeHtml — на
      // случай, если в захваченной группе $1 окажется что-то экзотическое
      // (защита от подмены allowlist'а через postprocess).
      let html = safeMarkdown(msg);
      html = html.replace(
        /<strong>Риски и нюансы:?<\/strong>([\s\S]*?)(?=<strong>|<h|$)/gi,
        '<div class="chat-blockquote chat-blockquote--danger"><strong>Риски и нюансы</strong>$1</div>'
      );
      return safeHtml(html);
    },
    formatTimestamp(ts) {
      return moment(ts).format("HH:mm");
    },
    attachScrollListener() {
      const el = this.$refs.chatBody;
      if (!el) return;
      this._onScroll = () => {
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        this.isAtBottom = remaining < el.clientHeight * 0.3;
      };
      el.addEventListener("scroll", this._onScroll, { passive: true });
      this._onScroll();
    },
    detachScrollListener() {
      const el = this.$refs.chatBody;
      if (el && this._onScroll) {
        el.removeEventListener("scroll", this._onScroll);
      }
    },
    _animateScroll(target, duration) {
      const el = this.$refs.chatBody;
      if (!el) return;
      if (this._scrollRAF) cancelAnimationFrame(this._scrollRAF);
      if (this._scrollCancel) this._scrollCancel();
      const start = el.scrollTop;
      if (target - start < 2) return;
      const startTime = performance.now();
      let cancelled = false;
      const onUserScroll = () => {
        cancelled = true;
      };
      el.addEventListener("wheel", onUserScroll, { passive: true });
      el.addEventListener("touchstart", onUserScroll, { passive: true });
      el.addEventListener("keydown", onUserScroll);
      this._scrollCancel = () => {
        cancelled = true;
        el.removeEventListener("wheel", onUserScroll);
        el.removeEventListener("touchstart", onUserScroll);
        el.removeEventListener("keydown", onUserScroll);
      };
      const step = (now) => {
        if (cancelled) {
          this._scrollCancel?.();
          this._scrollCancel = null;
          return;
        }
        const t = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        el.scrollTop = start + (target - start) * eased;
        if (t < 1) {
          this._scrollRAF = requestAnimationFrame(step);
        } else {
          this._scrollCancel?.();
          this._scrollCancel = null;
        }
      };
      this._scrollRAF = requestAnimationFrame(step);
    },
    slowScrollToBottom(duration = 700) {
      const el = this.$refs.chatBody;
      if (!el) return;
      const target = el.scrollHeight - el.clientHeight;
      this._animateScroll(target, duration);
    },
    slowScrollPartial(fraction = 0.5, duration = 300) {
      const el = this.$refs.chatBody;
      if (!el) return;
      const maxScroll = el.scrollHeight - el.clientHeight;
      const target = Math.min(
        el.scrollTop + el.clientHeight * fraction,
        maxScroll
      );
      this._animateScroll(target, duration);
    },
    scrollLastMessageToTop() {
      const el = this.$refs.chatBody;
      if (!el) return;
      const groups = el.querySelectorAll(".chat-message-group");
      const last = groups[groups.length - 1];
      if (!last) return;
      const containerRect = el.getBoundingClientRect();
      const messageRect = last.getBoundingClientRect();
      const target = el.scrollTop + (messageRect.top - containerRect.top) - 16;
      el.scrollTo({ top: target, behavior: "smooth" });
    },
    ensurePlaceholderVisible() {
      const el = this.$refs.chatBody;
      if (!el) return;
      const groups = el.querySelectorAll(".chat-message-group");
      const last = groups[groups.length - 1];
      if (!last) return;
      const dock = el.querySelector(".chat-dock");
      const dockHeight = dock?.offsetHeight ?? 150;
      const safeBottom = el.clientHeight - (dockHeight + 40);
      const containerRect = el.getBoundingClientRect();
      const lastRect = last.getBoundingClientRect();
      const lastBottomRelative = lastRect.bottom - containerRect.top;
      if (lastBottomRelative > safeBottom) {
        const delta = lastBottomRelative - safeBottom;
        el.scrollTo({
          top: el.scrollTop + delta,
          behavior: "smooth",
        });
      }
    },
    async sendMessage(text) {
      const msg = text || this.message;
      if (!msg?.trim() || this.loading) return;
      this.loading = true;
      this.abortController = new AbortController();
      this.message = "";
      this.$nextTick(() => this.adjustTextareaHeight());
      if (this.currentSession && !this.currentSession.messages.length) {
        this.currentSession.title = msg;
      }
      this.history.push({
        sender: "user",
        message: msg,
        timestamp: Date.now(),
      });
      await this.$nextTick();
      this.scrollLastMessageToTop();
      const placeholder = {
        sender: "assistant",
        message: "",
        timestamp: `${Date.now()}_${Math.floor(Math.random() * 100000)}`,
        isTyping: true,
      };
      this.history.push(placeholder);
      await this.$nextTick();
      this.ensurePlaceholderVisible();
      try {
        const response = await sendChat(msg, this.currentSession?.id, {
          signal: this.abortController.signal,
        });
        const data = response.data;
        const idx = this.history.indexOf(placeholder);
        if (idx !== -1) this.history.splice(idx, 1);
        this.history.push({
          sender: "assistant",
          timestamp: Date.now(),
          message:
            data.message || "Извините, не удалось обработать ваш запрос.",
          status: data.status || null,
          categories: data.categories || null,
          question_number: data.question_number || null,
          total_questions: data.total_questions || null,
          documents: data.documents || null,
          steps: data.steps || null,
          actions: data.actions || null,
        });
        await this.$nextTick();
        this.slowScrollPartial(0.5);
      } catch (error) {
        if (error.name !== "AbortError" && error.code !== "ERR_CANCELED") {
          console.error(error);
          const idx = this.history.indexOf(placeholder);
          if (idx !== -1) this.history.splice(idx, 1);
          this.history.push({
            sender: "assistant",
            timestamp: Date.now(),
            message: "Мне не удалось обработать ваш запрос. Попробуйте позже.",
          });
        }
      } finally {
        this.loading = false;
        this.abortController = null;
        if (this.currentSession) {
          this.currentSession.messages = this.history.filter(
            (m) => !m.isTyping
          );
        }
      }
    },
    handleEnter(e) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    },
    adjustTextareaHeight() {
      const ta = this.$refs.textarea;
      if (!ta) return;
      ta.style.height = "auto";
      ta.style.height = Math.min(ta.scrollHeight, 200) + "px";
    },
    toggleVoice() {
      if (this.inputMode === "text") {
        if (!navigator.mediaDevices?.getUserMedia) return;
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then(() => (this.inputMode = "voice"))
          .catch(() => (this.inputMode = "text"));
      } else {
        this.inputMode = "text";
      }
    },
    handleSttResult(text) {
      this.inputMode = "text";
      if (text) {
        this.message = text;
        this.sendMessage();
      }
    },
    cancelProcessing(silent = false) {
      if (!this.abortController) return;
      this.abortController.abort();
      this.abortController = null;
      this.loading = false;
      if (this.currentSession) {
        this.currentSession.messages = this.currentSession.messages.filter(
          (m) => !m.isTyping
        );
      }
      if (!silent) {
        this.history.push({
          sender: "assistant",
          message: this.$t(
            "Выполнение вашего запроса было остановлено. Отправьте новое сообщение."
          ),
          timestamp: Date.now(),
        });
      }
    },
    newChat() {
      if (this.history.length > 0) this.createNewSession();
      this.message = "";
      this.inputMode = "text";
    },
    createNewSession() {
      this.cancelProcessing(true);
      const s = {
        id: Date.now(),
        createdAt: Date.now(),
        title: this.$t("Новый чат"),
        messages: [],
      };
      this.sessions.unshift(s);
      this.currentSession = s;
    },
    setActiveSession(session) {
      if (this.currentSession && !this.currentSession.messages.length) {
        this.sessions = this.sessions.filter(
          (s) => s.id !== this.currentSession.id
        );
      }
      this.currentSession = session;
    },
    deleteSession(session) {
      this.sessions = this.sessions.filter((s) => s !== session);
      if (this.currentSession === session) {
        this.currentSession = null;
        this.createNewSession();
      }
    },
    clearChat() {
      this.sessions = [];
      this.currentSession = null;
      localStorage.removeItem("client_ai_chat_sessions");
      this.createNewSession();
    },
    handleSelectCategory(cat) {
      this.navigateToAction(cat.code);
    },
    async navigateToAction(code) {
      const data = await getTemplateItem(code);
      if (data?.payload?.category_code === CATEGORY_CODES.SVID_PRAVO_NASLED) {
        this.$router.push({ name: "hereditary_case_new", query: { code } });
      } else {
        this.$router.push({ name: "notarial_action_new", query: { code } });
      }
    },
  },
};
</script>

<template>
  <div class="client-ai-chat">
    <ClientAiSideBar
      :sessions="sessions"
      :current-session="currentSession"
      :collapsed="sidebarCollapsed"
      back-route="client_dashboard"
      @newChat="newChat"
      @update:currentSession="setActiveSession"
      @delete-session="deleteSession"
      @clearHistory="clearChat"
      @update:collapsed="sidebarCollapsed = $event"
    />

    <div class="chat-main">
      <div class="chat-body" ref="chatBody">
        <div class="chat-inner">
          <div v-if="!history.length" class="chat-hero">
            {{ $t("Чем я могу помочь?") }}
          </div>

          <div v-if="history.length" class="chat-messages">
            <div
              v-for="item in history"
              :key="item.timestamp"
              class="chat-message-group"
            >
              <div
                v-if="item.sender === 'user'"
                class="chat-bubble chat-bubble--user"
              >
                <div
                  v-html="renderMessage(item.message)"
                  v-if="item.message"
                ></div>
              </div>

              <div
                v-if="item.sender === 'assistant'"
                class="chat-bubble chat-bubble--assistant"
              >
                <div v-if="item.isTyping" class="chat-loading">
                  <el-icon class="is-loading"><Loading /></el-icon>
                  <span>{{ $t("Подбираю подходящие услуги") }}...</span>
                </div>

                <div
                  v-if="item.question_number && item.total_questions"
                  class="chat-progress"
                >
                  {{ $t("Вопрос") }} {{ item.question_number }} {{ $t("из") }}
                  {{ item.total_questions }}
                </div>

                <div
                  v-if="item.message && !item.isTyping"
                  class="chat-assistant-text prose prose-sm max-w-none prose-neutral dark:prose-invert"
                  v-html="renderMessage(item.message)"
                ></div>

                <div
                  v-if="item.documents && Object.keys(item.documents).length"
                  class="chat-documents"
                >
                  <div class="chat-section-title">
                    {{ $t("Необходимые документы") }}
                  </div>
                  <div
                    v-for="(docs, group) in item.documents"
                    :key="group"
                    class="chat-doc-group"
                  >
                    <div class="chat-doc-group-title">{{ group }}</div>
                    <div class="chat-doc-list">
                      <div
                        v-for="(doc, idx) in docs"
                        :key="idx"
                        class="chat-doc-item"
                      >
                        {{ doc }}
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="item.steps && item.steps.length" class="chat-steps">
                  <div class="chat-section-title">
                    {{ $t("Этапы процесса") }}
                  </div>
                  <div class="chat-steps-list">
                    <div
                      v-for="(step, idx) in item.steps"
                      :key="idx"
                      class="chat-step-item"
                    >
                      {{ idx + 1 }}. {{ step }}
                    </div>
                  </div>
                </div>
                <template
                  v-if="
                    item.status === 'done' &&
                    item.categories &&
                    item.categories.length
                  "
                >
                  <div class="chat-forms-header">
                    {{ $t("Найдено услуг") }}: {{ item.categories.length }}
                  </div>
                  <div class="chat-forms-list">
                    <div
                      v-for="cat in item.categories"
                      :key="cat.code"
                      class="chat-form-card chat-form-card--static"
                    >
                      <div class="chat-form-name">{{ cat.name }}</div>
                      <div class="chat-form-code">{{ cat.code }}</div>
                    </div>
                  </div>
                </template>
              </div>
              <div
                v-if="!item.isTyping"
                class="chat-timestamp"
                :class="item.sender"
              >
                {{ formatTimestamp(item.timestamp) }}
              </div>
            </div>
          </div>

          <div class="chat-dock">
            <button
              v-if="!isAtBottom && history.length"
              class="chat-scroll-bottom"
              @click="slowScrollToBottom(700)"
              :title="$t('Прокрутить вниз')"
              aria-label="Scroll to bottom"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 5v14M19 12l-7 7-7-7" />
              </svg>
            </button>
            <div class="chat-dock-container">
              <div v-if="inputMode === 'text'" class="chat-form">
                <textarea
                  v-model="message"
                  ref="textarea"
                  rows="1"
                  @keydown="handleEnter"
                  :placeholder="$t('Введите Ваш вопрос ...')"
                ></textarea>
                <div class="chat-form-actions">
                  <div></div>
                  <div class="flex items-center gap-2">
                    <button
                      v-if="loading"
                      @click="cancelProcessing()"
                      class="cancel-btn"
                    >
                      <SvgIcon name="huge-close" class="w-4 h-4" />
                    </button>
                    <template v-else>
                      <button
                        class="voice-btn"
                        @click="toggleVoice"
                        :title="$t('Записать голос')"
                      >
                        <SvgIcon name="sli-microphone" class="w-5 h-5" />
                      </button>
                      <button
                        class="send-btn"
                        :class="{ active: message.length > 0 }"
                        @click="sendMessage()"
                        :title="$t('Отправить')"
                      >
                        <SvgIcon name="sli-send" class="w-5 h-5" />
                      </button>
                    </template>
                  </div>
                </div>
              </div>
              <SpeechToText v-else @finish="handleSttResult" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.client-ai-chat {
  @apply flex h-full w-full border border-border rounded-lg overflow-hidden;
}
.chat-main {
  @apply flex-1 flex flex-col;
  position: relative;
  min-width: 0;
  min-height: 0;
}
.chat-body {
  @apply w-full relative flex justify-center overflow-y-auto;
  flex: 1 1 0;
  min-height: 0;
  padding: 0 24px;
}
.chat-inner {
  @apply w-full flex flex-col;
  max-width: 780px;
  margin-top: auto;
  margin-bottom: auto;
}
.chat-hero {
  @apply text-2xl font-bold text-center text-foreground;
  padding: 16px 0 32px;
}

.chat-dock {
  position: sticky;
  bottom: 0;
  z-index: 10;
}
.chat-dock-container {
  position: relative;
  width: 100%;
  padding-bottom: max(16px, env(safe-area-inset-bottom));
  z-index: 1;
  background: hsl(var(--background));
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}
.chat-scroll-bottom {
  position: absolute;
  left: 50%;
  bottom: 100%;
  transform: translate(-50%, -8px);
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid hsl(var(--border));
  background: hsl(var(--background));
  color: hsl(var(--foreground));
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  //z-index: 2;
  box-shadow: 0 4px 12px rgb(0 0 0 / 0.08), 0 1px 3px rgb(0 0 0 / 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.15s ease;

  &:hover {
    transform: translate(-50%, -10px);
    background: hsl(var(--muted));
    //box-shadow: 0 6px 16px rgb(0 0 0 / 0.12), 0 2px 4px rgb(0 0 0 / 0.08);
  }

  svg {
    width: 16px;
    height: 16px;
  }
}
.chat-messages {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 16px 0;
}
.chat-message-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.chat-bubble {
  @apply bg-muted;
  padding: 14px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.5;
  max-width: 85%;
  position: relative;
}
.chat-bubble--user {
  padding: 6px 14px;
  align-self: flex-end;
  background: hsl(var(--primary));
  color: hsl(var(--primary-foreground));
  border-bottom-right-radius: 4px;
}
.chat-bubble--assistant {
  align-self: flex-start;
  @apply bg-muted/40 border border-border;
  color: hsl(var(--foreground));
  border-bottom-left-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 85%;
  max-width: 85%;
}
.chat-assistant-text {
  :deep(p) {
    margin-top: 0;
    margin-bottom: 8px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  :deep(ul),
  :deep(ol) {
    font-size: inherit;
  }
  :deep(li) {
    font-size: inherit;
  }
  :deep(hr) {
    border-color: hsl(var(--border));
  }
  :deep(blockquote) {
    border-left: 3px solid hsl(var(--primary));
    background: hsl(var(--primary) / 0.06);
    border-radius: 0 8px 8px 0;
    padding: 10px 14px;
    margin: 8px 0;
    font-style: normal;
    color: hsl(var(--foreground));
  }
  :deep(blockquote p) {
    margin-bottom: 0;
  }
  :deep(.chat-blockquote--danger) {
    border-left: 3px solid #ef4444;
    background: rgba(239, 68, 68, 0.06);
    border-radius: 0 8px 8px 0;
    padding: 10px 14px;
    margin: 10px 0;
  }
  :deep(.chat-blockquote--danger strong) {
    color: #ef4444;
  }
}
.chat-progress {
  font-size: 11px;
  font-weight: 600;
  color: hsl(var(--primary));
  padding: 4px 8px;
  background: hsl(var(--primary) / 0.1);
  border-radius: 6px;
  width: fit-content;
}
.chat-section-title {
  font-size: 15px;
  font-weight: 600;
  color: hsl(var(--foreground));
  margin-top: 8px;
  margin-bottom: 4px;
}
.chat-documents {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.chat-doc-group {
  padding: 8px 12px;
  border: 1px solid hsl(var(--border));
  border-radius: 8px;
  background: hsl(var(--background));
}
.chat-doc-group-title {
  font-size: 14px;
  font-weight: 600;
  color: hsl(var(--foreground));
  margin-bottom: 4px;
}
.chat-doc-list {
  padding-left: 16px;
  font-size: 13px;
  color: hsl(var(--muted-foreground));
  .chat-doc-item {
    margin-bottom: 2px;
    &::before {
      content: "• ";
    }
  }
}
.chat-steps {
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-left: 3px solid #f59e0b;
  background: rgba(245, 158, 11, 0.06);
  border-radius: 0 8px 8px 0;
  padding: 10px 14px;
  margin: 10px 0;
}
.chat-steps-list {
  padding-left: 16px;
  font-size: 14px;
  color: hsl(var(--muted-foreground));
  .chat-step-item {
    margin-bottom: 4px;
  }
}
.chat-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}
.chat-timestamp {
  font-size: 11px;
  color: hsl(var(--muted-foreground));
  &.user {
    align-self: flex-end;
  }
  &.assistant {
    align-self: flex-start;
  }
}
.chat-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: hsl(var(--muted-foreground));
  .is-loading {
    display: inline-flex;
  }
}
.chat-forms-header {
  font-size: 13px;
  color: hsl(var(--muted-foreground));
}
.chat-forms-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.chat-form-card {
  text-align: left;
  padding: 10px 12px;
  border: 1px solid hsl(var(--border));
  border-radius: 8px;
  background: hsl(var(--background));
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;
  &:hover {
    border-color: hsl(var(--primary));
    box-shadow: 0 2px 8px hsl(var(--primary) / 0.08);
    transform: translateY(-1px);
  }
}
.chat-form-card--static {
  cursor: default;
  &:hover {
    border-color: hsl(var(--border));
    box-shadow: none;
    transform: none;
  }
}
.chat-form-name {
  font-size: 13px;
  font-weight: 600;
  color: hsl(var(--foreground));
  line-height: 1.3;
}
.chat-form-code {
  font-size: 11px;
  color: hsl(var(--muted-foreground));
  margin-top: 2px;
  line-height: 1.35;
}
.chat-form {
  display: flex;
  flex-direction: column;
  border: 1px solid hsl(var(--border));
  border-radius: 16px;
  background: hsl(var(--background));
  transition: border-color 0.2s ease;

  textarea {
    outline: none;
    width: 100%;
    padding: 18px 20px 8px;
    border: 0;
    border-radius: 24px;
    font-size: 14px;
    line-height: 1.5;
    resize: none;
    background: transparent;
    color: hsl(var(--foreground));
    overflow-y: auto;
    min-height: 60px;
    max-height: 200px;

    &::placeholder {
      color: hsl(var(--muted-foreground));
    }
  }

  .chat-form-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 14px 12px;
  }
  .cancel-btn {
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: hsl(var(--muted-foreground));
    color: hsl(var(--background));
    &:hover {
      opacity: 0.8;
    }
    svg {
      fill: none;
    }
  }
  .voice-btn {
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: transparent;
    color: hsl(var(--muted-foreground));
    &:hover {
      background: hsl(var(--muted));
    }
    svg {
      fill: none;
    }
  }
  .send-btn {
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 50%;

    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: hsl(var(--muted));
    color: hsl(var(--background));
    svg {
      fill: none;
    }
    &.active {
      background: hsl(var(--foreground));
    }
    &:hover {
      background: hsl(var(--foreground) / 0.8);
    }
  }
}
</style>
